import { supabase } from '../lib/supabase';

// ============================================
// AUTH SERVICES
// ============================================

export const authService = {
  async signUp(email, password, metadata = {}) {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: metadata },
    });
    if (error) throw error;
    return data;
  },

  async signIn(email, password) {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  },

  async signOut() {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  },

  async getSession() {
    const { data, error } = await supabase.auth.getSession();
    if (error) throw error;
    return data.session;
  },

  async getUser() {
    const { data, error } = await supabase.auth.getUser();
    if (error) throw error;
    return data.user;
  },

  onAuthStateChange(callback) {
    return supabase.auth.onAuthStateChange(callback);
  },
};

// ============================================
// PROFILE SERVICES
// ============================================

export const profileService = {
  async getProfile(userId) {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();
    if (error) throw error;
    return data;
  },

  async updateProfile(userId, updates) {
    const { data, error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', userId)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async createProfile(profile) {
    const { data, error } = await supabase.from('profiles').insert(profile).select().single();
    if (error) throw error;
    return data;
  },
};

// ============================================
// LOAN PRODUCTS SERVICES
// ============================================

export const productService = {
  async getProducts() {
    const { data, error } = await supabase
      .from('loan_products')
      .select('*')
      .eq('is_active', true)
      .order('name');
    if (error) throw error;
    return data;
  },

  async getProduct(id) {
    const { data, error } = await supabase
      .from('loan_products')
      .select('*')
      .eq('id', id)
      .single();
    if (error) throw error;
    return data;
  },
};

// ============================================
// LOAN APPLICATION SERVICES
// ============================================

export const applicationService = {
  async createApplication(applicationData) {
    const { data, error } = await supabase
      .from('loan_applications')
      .insert(applicationData)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async getApplication(id) {
    const { data, error } = await supabase
      .from('loan_applications')
      .select(`
        *,
        recommended_product:loan_products!recommended_product_id(*),
        selected_product:loan_products!selected_product_id(*),
        assigned_underwriter:profiles!assigned_underwriter_id(*)
      `)
      .eq('id', id)
      .single();
    if (error) throw error;
    return data;
  },

  async getMyApplications(userId) {
    const { data, error } = await supabase
      .from('loan_applications')
      .select(`
        *,
        selected_product:loan_products!selected_product_id(name, short_name, icon)
      `)
      .eq('applicant_id', userId)
      .order('updated_at', { ascending: false });
    if (error) throw error;
    return data;
  },

  async updateApplication(id, updates) {
    const { data, error } = await supabase
      .from('loan_applications')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async saveAssessmentAnswers(id, answers, recommendedProductId) {
    const { data, error } = await supabase
      .from('loan_applications')
      .update({
        assessment_answers: answers,
        recommended_product_id: recommendedProductId,
        status: 'product_recommended',
      })
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async submitToMarketplace(id) {
    const { data, error } = await supabase
      .from('loan_applications')
      .update({
        is_in_marketplace: true,
        marketplace_listed_at: new Date().toISOString(),
        status: 'in_marketplace',
      })
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return data;
  },
};

// ============================================
// UNDERWRITER SERVICES
// ============================================

export const underwriterService = {
  async getPipeline(underwriterId) {
    const { data, error } = await supabase
      .from('loan_applications')
      .select(`
        *,
        applicant:profiles!applicant_id(first_name, last_name, email),
        selected_product:loan_products!selected_product_id(name, short_name)
      `)
      .eq('assigned_underwriter_id', underwriterId)
      .in('status', ['submitted_for_underwriting', 'underwriting_in_progress', 'conditionally_cleared'])
      .order('submitted_at', { ascending: true });
    if (error) throw error;
    return data;
  },

  async getUnassignedApplications() {
    const { data, error } = await supabase
      .from('loan_applications')
      .select(`
        *,
        applicant:profiles!applicant_id(first_name, last_name, email),
        selected_product:loan_products!selected_product_id(name, short_name)
      `)
      .eq('status', 'submitted_for_underwriting')
      .is('assigned_underwriter_id', null)
      .order('submitted_at', { ascending: true });
    if (error) throw error;
    return data;
  },

  async claimApplication(applicationId, underwriterId) {
    const { data, error } = await supabase
      .from('loan_applications')
      .update({
        assigned_underwriter_id: underwriterId,
        status: 'underwriting_in_progress',
      })
      .eq('id', applicationId)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async getApplicationDetails(applicationId) {
    const { data, error } = await supabase
      .from('loan_applications')
      .select(`
        *,
        applicant:profiles!applicant_id(*),
        selected_product:loan_products!selected_product_id(*),
        documents(*),
        conditions(*)
      `)
      .eq('id', applicationId)
      .single();
    if (error) throw error;
    return data;
  },

  async addCondition(condition) {
    const { data, error } = await supabase.from('conditions').insert(condition).select().single();
    if (error) throw error;
    return data;
  },

  async updateCondition(id, updates) {
    const { data, error } = await supabase
      .from('conditions')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async approveApplication(applicationId, notes) {
    const { data, error } = await supabase
      .from('loan_applications')
      .update({
        status: 'cleared_for_marketplace',
        underwriter_notes: notes,
      })
      .eq('id', applicationId)
      .select()
      .single();
    if (error) throw error;
    return data;
  },
};

// ============================================
// LENDER SERVICES
// ============================================

export const lenderService = {
  async getMarketplaceApplications() {
    const { data, error } = await supabase.from('marketplace_applications').select('*');
    if (error) throw error;
    return data;
  },

  async getMyBids(lenderId) {
    const { data, error } = await supabase
      .from('lender_bids')
      .select(`
        *,
        application:loan_applications!application_id(anonymous_id, loan_amount, property_type)
      `)
      .eq('lender_id', lenderId)
      .order('created_at', { ascending: false });
    if (error) throw error;
    return data;
  },

  async placeBid(bid) {
    const { data, error } = await supabase.from('lender_bids').insert(bid).select().single();
    if (error) throw error;
    return data;
  },

  async updateBid(id, updates) {
    const { data, error } = await supabase
      .from('lender_bids')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async withdrawBid(id) {
    const { data, error } = await supabase
      .from('lender_bids')
      .update({ status: 'withdrawn' })
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return data;
  },
};

// ============================================
// DOCUMENT SERVICES
// ============================================

export const documentService = {
  async getDocuments(applicationId) {
    const { data, error } = await supabase
      .from('documents')
      .select('*')
      .eq('application_id', applicationId)
      .order('category', { ascending: true });
    if (error) throw error;
    return data;
  },

  async uploadDocument(applicationId, file, metadata) {
    // Upload file to storage
    const fileName = `${applicationId}/${Date.now()}_${file.name}`;
    const { data: uploadData, error: uploadError } = await supabase.storage
      .from('documents')
      .upload(fileName, file);
    if (uploadError) throw uploadError;

    // Get public URL
    const { data: urlData } = supabase.storage.from('documents').getPublicUrl(fileName);

    // Create document record
    const { data, error } = await supabase
      .from('documents')
      .insert({
        application_id: applicationId,
        file_url: urlData.publicUrl,
        file_name: file.name,
        file_size: file.size,
        file_type: file.type,
        uploaded_at: new Date().toISOString(),
        status: 'uploaded',
        ...metadata,
      })
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async updateDocumentStatus(id, status, reviewNotes) {
    const { data, error } = await supabase
      .from('documents')
      .update({
        status,
        review_notes: reviewNotes,
        reviewed_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return data;
  },
};

export default {
  auth: authService,
  profile: profileService,
  product: productService,
  application: applicationService,
  underwriter: underwriterService,
  lender: lenderService,
  document: documentService,
};
